/**
 * UI Manager
 * Handles UI initialization and event binding with proper cleanup
 * Manages difficulty buttons, volume controls, and other UI elements
 */

import { frontendLogger } from '../../shared/logger';
import { audioManager } from './audio';

type Difficulty = 'easy' | 'normal' | 'hard';
export type MapType = 'procedural' | 'fps-arena'; // Added fps-arena GLB map

export interface UIManagerOptions {
  onDifficultyChange?: (difficulty: Difficulty) => void;
  onMapChange?: (map: MapType) => void;
}

export class UIManager {
  private readonly onDifficultyChange?: (difficulty: Difficulty) => void;
  private readonly onMapChange?: (map: MapType) => void;
  private currentDifficulty: Difficulty = 'normal';
  private currentMap: MapType = 'procedural';

  // Store bound handlers for cleanup
  private readonly difficultyHandlers = new Map<HTMLElement, (ev: Event) => void>();
  private readonly mapHandlers = new Map<HTMLElement, (ev: Event) => void>();
  private volumeHandler?: (e: Event) => void;
  private musicStartHandler?: () => void;

  constructor(options: UIManagerOptions = {}) {
    this.onDifficultyChange = options.onDifficultyChange;
    this.onMapChange = options.onMapChange;

    // Load saved map preference
    const savedMap = localStorage.getItem('selectedMap') as MapType | null;
    if (savedMap === 'procedural' || savedMap === 'fps-arena') {
      this.currentMap = savedMap;
    }

    this.setupDifficultyButtons();
    this.setupMapButtons();
    this.setupVolumeControls();
    this.setupBackgroundMusic();
    frontendLogger.info('UIManager initialized', { map: this.currentMap });
  }

  private setupDifficultyButtons(): void {
    const difficultyButtons = document.querySelectorAll('[data-difficulty]');

    difficultyButtons.forEach((btn) => {
      const handler = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        const element = ev.currentTarget as HTMLElement;
        const level = element.dataset.difficulty as Difficulty | undefined;
        if (level) {
          this.setDifficulty(level);
        }
      };

      btn.addEventListener('click', handler);
      this.difficultyHandlers.set(btn as HTMLElement, handler);
    });

    this.updateDifficultyUI(this.currentDifficulty);
  }

  private setupMapButtons(): void {
    const mapButtons = document.querySelectorAll('[data-map]');

    mapButtons.forEach((btn) => {
      const handler = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        const element = ev.currentTarget as HTMLElement;
        const mapType = element.dataset.map as MapType | undefined;
        if (mapType) {
          this.setMap(mapType);
        }
      };

      btn.addEventListener('click', handler);
      this.mapHandlers.set(btn as HTMLElement, handler);
    });

    this.updateMapUI(this.currentMap);
  }

  private setupVolumeControls(): void {
    const volumeSlider = document.getElementById('volume-slider') as HTMLInputElement;
    const volumeValue = document.getElementById('volume-value');

    if (volumeSlider && volumeValue) {
      this.volumeHandler = (e: Event) => {
        const volume = parseInt((e.target as HTMLInputElement).value, 10) / 100;
        audioManager.setMasterVolume(volume);
        volumeValue.textContent = `${(e.target as HTMLInputElement).value}%`;
      };

      volumeSlider.addEventListener('input', this.volumeHandler);

      // Set initial volume
      audioManager.setMasterVolume(0.7);
      frontendLogger.debug('Volume controls initialized');
    }
  }

  private setupBackgroundMusic(): void {
    let musicStarted = false;

    this.musicStartHandler = () => {
      if (!musicStarted) {
        audioManager.startAmbientMusic();
        musicStarted = true;
        frontendLogger.debug('Background music started');
      }
    };

    // Use { once: true } for automatic cleanup
    document.addEventListener('click', this.musicStartHandler, { once: true });
    document.addEventListener('keydown', this.musicStartHandler, { once: true });
  }

  private updateDifficultyUI(level: Difficulty): void {
    const difficultyButtons = document.querySelectorAll('[data-difficulty]');
    difficultyButtons.forEach((btn) => {
      if (btn instanceof HTMLElement) {
        const isActive = btn.dataset.difficulty === level;
        btn.classList.toggle('active', isActive);
        btn.style.opacity = isActive ? '1' : '0.6';
      }
    });
  }

  public setDifficulty(level: Difficulty): void {
    this.currentDifficulty = level;
    this.updateDifficultyUI(level);
    this.onDifficultyChange?.(level);
    audioManager.play('menu_click');
    frontendLogger.info('Difficulty changed', { level });
  }

  public getDifficulty(): Difficulty {
    return this.currentDifficulty;
  }

  private updateMapUI(mapType: MapType): void {
    const mapButtons = document.querySelectorAll('[data-map]');
    mapButtons.forEach((btn) => {
      if (btn instanceof HTMLElement) {
        const isActive = btn.dataset.map === mapType;
        btn.classList.toggle('active', isActive);
        btn.style.opacity = isActive ? '1' : '0.6';
      }
    });
  }

  public setMap(mapType: MapType): void {
    this.currentMap = mapType;
    this.updateMapUI(mapType);
    localStorage.setItem('selectedMap', mapType);
    this.onMapChange?.(mapType);
    audioManager.play('menu_click');
    frontendLogger.info('Map changed', { map: mapType });
  }

  public getMap(): MapType {
    return this.currentMap;
  }

  /**
   * Cleanup method to remove event listeners
   */
  public destroy(): void {
    // Remove difficulty button handlers
    this.difficultyHandlers.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.difficultyHandlers.clear();

    // Remove map button handlers
    this.mapHandlers.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.mapHandlers.clear();

    // Remove volume handler
    if (this.volumeHandler) {
      const volumeSlider = document.getElementById('volume-slider');
      if (volumeSlider) {
        volumeSlider.removeEventListener('input', this.volumeHandler);
      }
    }

    // Note: musicStartHandler uses { once: true } so it auto-cleans up

    frontendLogger.info('UIManager destroyed');
  }
}
